import { RawDraftContentState } from "draft-js";
import React from "react";
import styled from "styled-components/macro";
import { IComponent } from "../Component";
import DraftJSView from "./draftjs-view";
import "./RichTextExt.css";
import { bsrBlue } from "./bsr-defs";

export const RICH_TEXT_COMPONENT = "rich_text";

export interface RichTextComponentSpec {
  type: typeof RICH_TEXT_COMPONENT;
  content: RawDraftContentState;
  id?: string;
}

export const EditorStyle = styled.div`
  margin-top: 15px;
  white-space: pre-wrap;

  /* Initialize counter at the container level */
  counter-reset: item;

  /* Reduce paragraph spacing for tighter text */
  p {
    margin: 0.25em 0;
  }

  /* Adjacent paragraphs should have minimal spacing */
  p + p {
    margin-top: 0.25em;
  }

  /* Reset counter when a paragraph (or other non-list element) appears */
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  blockquote,
  pre {
    counter-reset: item;
  }

  ul {
    margin: 0.5em 0;
    padding: 0 0 0 1.5em;
    list-style: none;
  }

  ul > li {
    position: relative;
    padding-left: 0.4em;
    margin: 0.1em 0;
  }

  ul > li:before {
    content: "\\f054"; /* FontAwesome Unicode */
    color: ${bsrBlue};
    font-family: RichTextListIcon;
    position: absolute;
    left: -1em;
    top: 0.15em;
    transform: scale(0.9);
  }

  ol {
    margin: 0.5em 0;
    padding: 0 0 0 1.5em;
    list-style-type: none;
    /* Don't reset counter here - let it continue from container or previous ol */
  }

  ol > li {
    display: block;
    margin: 0.1em 0;
  }

  ol > li:before {
    content: counter(item) ". ";
    counter-increment: item;
    color: ${bsrBlue};
    display: block;
    position: relative;
    max-height: 0px;
    left: -1.3em;
    top: 0.05em;
  }

  /* Nested unordered lists - tighter spacing */
  ul ul,
  ol ul {
    margin: 0.1em 0;
    padding-left: 1.5em;
  }

  /* Nested ordered lists inside unordered - need proper indentation, own counter */
  ul ol {
    margin: 0.1em 0;
    padding-left: 1.8em;
    counter-reset: subitem;
  }

  ul ol > li:before {
    content: counter(subitem) ". ";
    counter-increment: subitem;
  }

  /* Nested ordered lists inside ordered - own counter */
  ol ol {
    margin: 0.1em 0;
    padding-left: 1.5em;
    counter-reset: subitem;
  }

  ol ol > li:before {
    content: counter(subitem) ". ";
    counter-increment: subitem;
  }

  /* Third level nesting */
  ul ul ul,
  ol ul ul,
  ul ol ul,
  ol ol ul {
    margin: 0.1em 0;
    padding-left: 1.5em;
  }

  ul ul ol,
  ol ul ol,
  ul ol ol,
  ol ol ol {
    margin: 0.1em 0;
    padding-left: 1.8em;
    counter-reset: subsubitem;
  }

  ul ul ol > li:before,
  ol ul ol > li:before,
  ul ol ol > li:before,
  ol ol ol > li:before {
    content: counter(subsubitem) ". ";
    counter-increment: subsubitem;
  }

  /* Nested lists directly after li content need proper spacing */
  li > ul,
  li > ol {
    margin-top: 0.2em;
  }

  /* Link styles */
  a {
    color: ${bsrBlue};
    text-decoration: underline;
  }

  /* Indentation depth classes for Draft.js editor */
  .list-depth-0 {
    margin-left: 0;
  }
  .list-depth-1 {
    margin-left: 1.5em;
  }
  .list-depth-2 {
    margin-left: 3em;
  }
`;

const RichText = (props: { spec: RichTextComponentSpec }) => {
  return (
    <EditorStyle>
      <DraftJSView data={props.spec.content} />
    </EditorStyle>
  );
};

export const RichTextComponent: IComponent<RichTextComponentSpec> = {
  component: RichText,
};
